import React, { useState, useEffect } from 'react';
import { MessageSquare, Star, Clock, AlertCircle, CheckCircle2, ChevronDown, ChevronRight, Copy, Check } from 'lucide-react';
import { adminService } from '../../services/adminService';
import { AdminFeedback, AdminSessionFeedbacksResponse } from '../../types/admin';
import { FeedbackData } from '../../types/feedback';

interface SessionFeedbackProps {
  sessionId: string;
  className?: string;
}

export const SessionFeedback: React.FC<SessionFeedbackProps> = ({ 
  sessionId, 
  className = '' 
}) => {
  const [feedbacks, setFeedbacks] = useState<AdminFeedback[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isExpanded, setIsExpanded] = useState(true);
  const [isCopied, setIsCopied] = useState(false);

  useEffect(() => {
    const loadFeedbacks = async () => {
      if (!sessionId) return;
      
      setLoading(true);
      setError(null);
      
      try {
        const response: AdminSessionFeedbacksResponse = await adminService.getSessionFeedbacks(sessionId, true);
        setFeedbacks(response.feedbacks || []);
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load session feedback';
        setError(errorMessage);
        console.error('Failed to load session feedback:', err);
      } finally {
        setLoading(false);
      }
    };

    loadFeedbacks();
  }, [sessionId]);

  const copyFeedbacksToClipboard = async () => {
    if (feedbacks.length === 0) return;

    const formatFeedback = (feedback: AdminFeedback) => {
      const data = feedback.feedbackData as FeedbackData;
      const lines: string[] = [];
      
      lines.push(`Session Feedback (v${feedback.version}) - ${new Date(feedback.createdAt).toLocaleDateString()}`);
      if (data.testType) lines.push(`Test Type: ${data.testType}`);
      
      // Helper to format rating
      const formatRating = (value: number | 'not-applicable' | undefined) => {
        if (value === 'not-applicable' || value === 0) return 'N/A';
        if (typeof value === 'number') return `${value}`;
        return 'No rating';
      };

      // Collect all ratings and comments
      const fields = [
        { label: 'Welcome Message', rating: data.welcomeMessageRating, comment: data.welcomeMessageComment },
        { label: 'Wellbeing Check', rating: data.wellbeingCheckRating, comment: data.wellbeingCheckComment },
        { label: 'Understanding & Advice', rating: data.understandingAdviceRating, comment: data.understandingAdviceComment },
        { label: 'Advice Relevance', rating: data.adviceRelevanceRating, comment: data.adviceRelevanceComment },
        { label: 'Helpfulness', rating: data.helpfulnessRating, comment: data.helpfulnessComment },
        { label: 'Website Referral', rating: data.websiteReferralRating, comment: data.websiteReferralComment },
        { label: 'Officer Referral', rating: data.officerReferralRating, comment: data.officerReferralComment },
        { label: 'Conversation Style', rating: data.conversationStyleRating, comment: data.conversationStyleComment },
        { label: 'Adaptability', rating: data.adaptabilityRating, comment: data.adaptabilityComment },
        { label: 'Response Timing', rating: data.responseTimingRating, comment: data.responseTimingComment },
        { label: 'Overall Experience', rating: data.overallRating, comment: data.overallComment },
        { label: 'Likely to Use Again', rating: data.likelyToUseAgainRating, comment: data.likelyToUseAgainComment },
        { label: 'Recommend to Colleagues', rating: data.ratingFriends, comment: undefined },
        { label: 'Recommend to Customers', rating: data.ratingCustomers, comment: undefined }
      ];

      fields.forEach(field => {
        if (field.rating !== undefined) {
          const ratingText = formatRating(field.rating);
          const commentText = field.comment ? ` - "${field.comment}"` : '';
          lines.push(`${field.label}: ${ratingText}${commentText}`);
        }
      });

      // Add additional comments
      if (data.otherComments) lines.push(`Other Comments: "${data.otherComments}"`);
      if (data.responseQualityIssues) lines.push(`Quality Issues: "${data.responseQualityIssues}"`);
      if (data.otherSuggestions) lines.push(`Suggestions: "${data.otherSuggestions}"`);

      return lines.join('\n');
    };

    const formattedFeedbacks = feedbacks
      .map(formatFeedback)
      .join('\n\n---\n\n');

    try {
      await navigator.clipboard.writeText(formattedFeedbacks);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy feedback:', err);
    }
  };

  const renderRatingField = (label: string, value: number | 'not-applicable' | undefined, fullQuestion?: string) => {
    if (value === undefined || value === null) return null;
    
    if (value === 'not-applicable' || value === 0) {
      return (
        <div className="flex items-center justify-between py-2" title={fullQuestion}>
          <span className="text-sm font-medium text-gray-700 cursor-help">{label}</span>
          <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded">
            N/A
          </span>
        </div>
      );
    }
    
    if (typeof value === 'number') {
      return (
        <div className="flex items-center justify-between py-2" title={fullQuestion}>
          <span className="text-sm font-medium text-gray-700 cursor-help">{label}</span>
          <div className="flex items-center">
            <Star className="w-4 h-4 text-yellow-400 mr-1" />
            <span className="text-sm font-medium">{value}</span>
          </div>
        </div>
      );
    }
    
    return null;
  };

  const renderCommentField = (comment: string | undefined) => {
    if (!comment || comment.trim() === '') return null;
    
    return (
      <div className="py-2">
        <p className="text-sm text-gray-600 bg-gray-50 p-2 rounded italic">"{comment}"</p>
      </div>
    );
  };

  const renderFeedbackSection = (feedback: AdminFeedback) => {
    const data = feedback.feedbackData as FeedbackData;
    
    const sections = [
      {
        title: 'Welcome & Initial Interaction',
        items: [
          { rating: 'welcomeMessageRating', comment: 'welcomeMessageComment', label: '1. Welcome Message', fullQuestion: 'How good do you experience the welcome message of Kim?' },
          { rating: 'wellbeingCheckRating', comment: 'wellbeingCheckComment', label: '2. Wellbeing Check', fullQuestion: 'How do you experience the wellbeing check in?' }
        ]
      },
      {
        title: 'Understanding & Advice',
        items: [
          { rating: 'understandingAdviceRating', comment: 'understandingAdviceComment', label: '3. Understanding & Advice', fullQuestion: 'How do you experience Kim´s ability to understand your issue and give correct advice?' },
          { rating: 'adviceRelevanceRating', comment: 'adviceRelevanceComment', label: '5. Advice Relevance', fullQuestion: 'How relevant do you experience the advice Kim gives in relation to your problem?' },
          { rating: 'helpfulnessRating', comment: 'helpfulnessComment', label: '10. Helpfulness', fullQuestion: 'How well does Kim offer help without overwhelming, judging, or ignoring signals?' }
        ]
      },
      {
        title: 'Referrals & Resources',
        items: [
          { rating: 'websiteReferralRating', comment: 'websiteReferralComment', label: '4. Website Referral', fullQuestion: 'How do you experience Kim´s ability to refer to the Renewmyday content (useful resources like video, audio, articles and recipies)?' },
          { rating: 'officerReferralRating', comment: 'officerReferralComment', label: '7. Officer Referral', fullQuestion: 'How do you experience Kim´s referral to a human Wellbeing Officer (timing of the referral, instructions of referral)?' }
        ]
      },
      {
        title: 'Communication Style',
        items: [
          { rating: 'conversationStyleRating', comment: 'conversationStyleComment', label: '8. Conversation Style', fullQuestion: 'How do you experience Kim´s way of speaking (Does it feel like a natural conversation with a human being)?' },
          { rating: 'adaptabilityRating', comment: 'adaptabilityComment', label: '9. Adaptability', fullQuestion: 'How well does Kim adapt her tone, complexity and flow in accordance with your questions?' },
          { rating: 'responseTimingRating', comment: 'responseTimingComment', label: '6. Response Timing', fullQuestion: 'How do you experience the timing of Kim´s replies (How fast you get a reply)?' }
        ]
      },
      {
        title: 'Overall Experience',
        items: [
          { rating: 'overallRating', comment: 'overallComment', label: '11. Overall Experience', fullQuestion: 'How would you rate your overall experience with Kim?' },
          { rating: 'likelyToUseAgainRating', comment: 'likelyToUseAgainComment', label: '12. Likely to Use Again', fullQuestion: 'How likely is it you address yourself to Kim next time you face a challenge?' }
        ]
      }
    ];

    return (
      <div key={feedback.id} className="border border-gray-200 rounded-lg p-4 mb-4">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center">
            <MessageSquare className="w-5 h-5 text-purple-600 mr-2" />
            <h4 className="text-lg font-medium text-gray-900">
              Session Feedback (v{feedback.version})
            </h4>
          </div>
          <div className="flex items-center text-sm text-gray-500">
            <Clock className="w-4 h-4 mr-1" />
            {new Date(feedback.createdAt).toLocaleDateString()}
          </div>
        </div>

        {data.testType && (
          <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded">
            <div className="flex items-center">
              <CheckCircle2 className="w-4 h-4 text-blue-600 mr-2" />
              <span className="text-sm font-medium text-blue-900">Test Type: {data.testType}</span>
            </div>
          </div>
        )}

        <div className="space-y-1">
          {sections.flatMap((section) => section.items).map((item) => {
            const hasContent = data[item.rating as keyof FeedbackData] !== undefined || 
              data[item.comment as keyof FeedbackData];
            
            if (!hasContent) return null;
            
            return (
              <div key={item.label}>
                {renderRatingField(item.label, data[item.rating as keyof FeedbackData] as any, item.fullQuestion)}
                {renderCommentField(data[item.comment as keyof FeedbackData] as string)}
              </div>
            );
          })}
        </div>

        {/* Legacy/Other fields */}
        {(data.otherComments || data.responseQualityIssues || data.otherSuggestions) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">Additional Comments</h5>
            {renderCommentField(data.otherComments)}
            {renderCommentField(data.responseQualityIssues)}
            {renderCommentField(data.otherSuggestions)}
          </div>
        )}

        {/* App feedback fields if present */}
        {(data.ratingFriends || data.ratingCustomers) && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <h5 className="text-sm font-semibold text-gray-800 mb-2">App Ratings</h5>
            {renderRatingField('Recommend to Friends', data.ratingFriends)}
            {renderRatingField('Recommend to Customers', data.ratingCustomers)}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
            Session Feedback
          </h3>
          <button 
            disabled
            className="p-2 rounded-lg transition-colors opacity-50"
            aria-label="Loading..."
          >
            <ChevronDown className="w-5 h-5 text-gray-500" />
          </button>
        </div>
        <div className="flex items-center justify-center">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-purple-600"></div>
          <span className="ml-2 text-gray-600">Loading feedback...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
            Session Feedback
          </h3>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isExpanded ? 'Collapse feedback' : 'Expand feedback'}
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
        {isExpanded && (
          <div className="flex items-center text-red-600">
            <AlertCircle className="w-5 h-5 mr-2" />
            <span className="text-sm">Error loading feedback: {error}</span>
          </div>
        )}
      </div>
    );
  }

  if (feedbacks.length === 0) {
    return (
      <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 flex items-center">
            <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
            Session Feedback (0)
          </h3>
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isExpanded ? 'Collapse feedback' : 'Expand feedback'}
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
        {isExpanded && (
          <div className="text-center">
            <MessageSquare className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p className="text-gray-600 text-sm">No feedback available for this session</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className={`bg-white rounded-lg shadow-sm border border-gray-200 p-6 ${className}`}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900 flex items-center">
          <MessageSquare className="w-5 h-5 mr-2 text-purple-600" />
          Session Feedback ({feedbacks.length})
        </h3>
        <div className="flex items-center space-x-2">
          {feedbacks.length > 0 && (
            <button
              onClick={copyFeedbacksToClipboard}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors flex items-center"
              aria-label="Copy all feedback to clipboard"
              title="Copy all feedback to clipboard"
            >
              {isCopied ? (
                <Check className="w-5 h-5 text-green-600" />
              ) : (
                <Copy className="w-5 h-5 text-gray-500" />
              )}
            </button>
          )}
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label={isExpanded ? 'Collapse feedback' : 'Expand feedback'}
          >
            {isExpanded ? (
              <ChevronDown className="w-5 h-5 text-gray-500" />
            ) : (
              <ChevronRight className="w-5 h-5 text-gray-500" />
            )}
          </button>
        </div>
      </div>
      
      {isExpanded && (
        <div className="space-y-4">
          {feedbacks.map(renderFeedbackSection)}
        </div>
      )}
    </div>
  );
};